"use client";

import * as Sentry from "@sentry/nextjs";

type SentryContextValue = boolean | number | string | null | undefined;

interface ReportCaughtErrorOptions {
  context?: Record<string, SentryContextValue>;
  expectedCodes?: readonly string[];
  expectedErrors?: readonly { code: string; message: string }[];
  operation: string;
}

export function getCaughtErrorCode(error: unknown) {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code;
  }

  return undefined;
}

export function shouldReportCaughtError(
  error: unknown,
  expectedCodes: readonly string[] = [],
  expectedErrors: ReportCaughtErrorOptions["expectedErrors"] = [],
) {
  const code = getCaughtErrorCode(error);
  if (code && expectedCodes.includes(code)) {
    return false;
  }

  const message =
    typeof error === "object" && error !== null && "message" in error
      ? error.message
      : undefined;
  return !expectedErrors.some(
    (expected) => expected.code === code && expected.message === message,
  );
}

function toError(error: unknown, operation: string) {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.trim()) {
    return new Error(error);
  }

  return new Error(`Caught error during ${operation}`);
}

export function reportCaughtError(
  error: unknown,
  options: ReportCaughtErrorOptions,
) {
  if (
    !shouldReportCaughtError(error, options.expectedCodes, options.expectedErrors)
  ) {
    return;
  }

  try {
    Sentry.withScope((scope) => {
      const errorCode = getCaughtErrorCode(error);
      scope.setTag("caught.operation", options.operation);
      // Shared SDK frames must not combine failures from different operations or codes.
      scope.setFingerprint([
        "{{ default }}",
        options.operation,
        ...(errorCode ? [errorCode] : []),
      ]);

      if (errorCode) {
        scope.setTag("caught.error_code", errorCode);
      }

      scope.setContext("caught_error", {
        operation: options.operation,
        ...options.context,
      });
      Sentry.captureException(toError(error, options.operation));
    });
  } catch {
    // Error reporting must not break the user action or its error handling.
  }
}
